/* Auto-detect color sibling products by handle prefix.
 *
 * Moon Linen splits each color into its own product
 *   linen-japanese-dress-cream
 *   linen-japanese-dress-lilac
 *   linen-japanese-dress-something
 * Rather than rely on per-product metafield setup, this script:
 *   1. reads each card's data-product-handle attribute
 *   2. derives a prefix by stripping the last hyphen-segment
 *   3. queries Shopify's /search/suggest.json for that prefix
 *   4. filters results whose handle ALSO starts with the same prefix
 *   5. renders tiny product-image thumbnails next to the existing
 *      .color-siblings (or creates it if absent), each linking to the
 *      sibling product's PDP.
 *
 * The script ONLY runs for cards that already lack >=2 manual entries in
 * .color-siblings - manual metafield entries always win.
 *
 * Caches results per-prefix in sessionStorage so the same group isn't
 * re-fetched across page navigations within a session. */

(function () {
  if (window.__colorSiblingsInited) return;
  window.__colorSiblingsInited = true;

  function init() {
    const cards = document.querySelectorAll('.card-wrapper[data-product-handle]');
    cards.forEach(processCard);
  }

  function processCard(card) {
    const handle = card.dataset.productHandle;
    if (!handle) return;
    // If the card already has >= 2 sibling swatches (manual metafield path),
    // don't re-fetch.
    const existing = card.querySelectorAll('.color-siblings__swatch');
    if (existing.length >= 2) return;

    // Strip last hyphen segment to get the group prefix.
    const lastDash = handle.lastIndexOf('-');
    if (lastDash < 0) return;
    const prefix = handle.slice(0, lastDash);
    // Avoid degenerate cases: short prefixes (1-2 chars) are noise.
    if (prefix.length < 4) return;

    fetchSiblings(prefix, handle).then((siblings) => {
      if (!siblings || siblings.length < 2) return;
      renderSiblings(card, siblings, handle);
 }).catch(() => {/* network errors swallowed - graceful degrade */});
  }

  // Storefront root in the shopper's language ("/" or "/de/"), so sibling links
  // and the search request stay on the German or Slovak store.
  const storefrontRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const cache = {};
  function fetchSiblings(prefix, currentHandle) {
    if (cache[prefix]) return Promise.resolve(cache[prefix]);
    const cacheKey = 'cs:' + storefrontRoot + prefix;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try { cache[prefix] = JSON.parse(cached); return Promise.resolve(cache[prefix]); }
      catch (_) {}
    }
    const q = encodeURIComponent(prefix.replace(/-/g, ' '));
    const url = storefrontRoot + 'search/suggest.json?q=' + q + '&resources%5Btype%5D=product&resources%5Blimit%5D=15';
    return fetch(url).then((r) => r.json()).then((data) => {
      const products = (data?.resources?.results?.products) || [];
      const filtered = products.filter((p) => {
        if (!p.handle) return false;
        // Must start with the prefix AND be ONE color step further (or be the
        // base itself). e.g. prefix='linen-japanese-dress' matches
        // 'linen-japanese-dress' and 'linen-japanese-dress-cream', but
        // NOT 'linen-japanese-dress-cream-extra-long' (which would be 2 segs).
        if (!p.handle.startsWith(prefix)) return false;
        const tail = p.handle.slice(prefix.length).replace(/^-/, '');
        // Tail is either '' (the base) or a single segment color name (no -).
        return tail === '' || !tail.includes('-');
      });
      cache[prefix] = filtered;
      try { sessionStorage.setItem(cacheKey, JSON.stringify(filtered)); } catch (_) {}
      return filtered;
    });
  }

  function renderSiblings(card, siblings, currentHandle) {
    // Reuse the existing .color-siblings container if present; otherwise
    // create one inside .card__information (after .price).
    let row = card.querySelector('.color-siblings');
    if (!row) {
      const info = card.querySelector('.card__information');
      if (!info) return;
      const priceEl = info.querySelector('.price');
      row = document.createElement('div');
      row.className = 'color-siblings color-siblings--sm color-siblings--auto';
      row.setAttribute('role', 'list');
      row.setAttribute('aria-label', window.accessibilityStrings?.availableColors || '');
      if (priceEl && priceEl.parentNode) {
        priceEl.parentNode.insertBefore(row, priceEl.nextSibling);
      } else {
        info.appendChild(row);
      }
    } else {
      // Wipe any existing single-swatch render so we replace it cleanly.
      row.innerHTML = '';
      row.classList.add('color-siblings--auto');
    }

    siblings.forEach((p) => {
      const isCurrent = p.handle === currentHandle;
      const tile = document.createElement(isCurrent ? 'span' : 'a');
      tile.className = 'color-siblings__thumb' + (isCurrent ? ' color-siblings__thumb--current' : '');
      if (!isCurrent) tile.href = storefrontRoot + 'products/' + p.handle;
      tile.title = p.title || p.handle;
      tile.setAttribute('aria-label', p.title || p.handle);
      tile.setAttribute('role', 'listitem');
      if (isCurrent) tile.setAttribute('aria-current', 'true');
      if (p.image) {
        const img = document.createElement('img');
        img.src = p.image.replace(/\.(jpg|jpeg|png|webp)(\?.*)?$/i, '_80x80.$1$2');
        img.alt = '';
        img.width = 28;
        img.height = 28;
        img.loading = 'lazy';
        tile.appendChild(img);
      }
      row.appendChild(tile);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-run when load-more appends new cards.
  const grid = document.getElementById('ProductGridContainer');
  if (grid) {
    new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.matches?.('.card-wrapper')) processCard(n);
          n.querySelectorAll?.('.card-wrapper[data-product-handle]').forEach(processCard);
        });
      });
    }).observe(grid, { childList: true, subtree: true });
  }
})();
